/**
 * Certificate Approval Gate
 *
 * Centralised gate that MUST approve before any X.509 certificate is created
 * or renewed. Prevents silent cert proliferation — every cert mint is visible.
 *
 * Usage:
 *   App startup:  setCertApprovalGate(ctx => confirm(`Create cert for ${ctx.email}?`))
 *   Tests:        setCertApprovalGate(() => Promise.resolve(true))  — or leave unset to block
 *
 * If no gate is configured, certificate generation throws immediately.
 * This makes tests fail-by-default when cert creation is unexpected.
 *
 * @contract CONTRACT:I4-SIGNATURE-TYPES.1.0
 * @see architecture/CONTRACT-I4-SIGNATURE-TYPES.1.0.md
 */

export type CertApprovalReason = 'new-keypair' | 'renewal' | 'ephemeral';

export interface CertApprovalContext {
  email: string;
  reason: CertApprovalReason;
}

type CertApprovalFn = (ctx: CertApprovalContext) => Promise<boolean>;

let _gate: CertApprovalFn | null = null;

/**
 * Install the approval gate. Must be called before any cert generation.
 * In the app, this shows a user-facing confirmation.
 * In tests, set to auto-approve or leave null to block.
 */
export function setCertApprovalGate(fn: CertApprovalFn): void {
  _gate = fn;
}

/**
 * Remove the gate (e.g. on teardown).
 */
export function clearCertApprovalGate(): void {
  _gate = null;
}

/**
 * Check whether a gate is currently installed.
 */
export function hasCertApprovalGate(): boolean {
  return _gate !== null;
}

/**
 * Request approval to create/renew a certificate.
 * Throws if no gate is configured or if the gate denies.
 */
export async function requireCertApproval(
  ctx: CertApprovalContext,
): Promise<void> {
  if (!_gate) {
    throw new Error(
      `[CertApprovalGate] Certificate generation BLOCKED — no approval gate configured. ` +
        `Email: ${ctx.email}, Reason: ${ctx.reason}. ` +
        `Call setCertApprovalGate() at app startup.`,
    );
  }
  const approved = await _gate(ctx);
  if (!approved) {
    throw new Error(
      `[CertApprovalGate] Certificate generation DENIED by user. ` +
        `Email: ${ctx.email}, Reason: ${ctx.reason}`,
    );
  }
}
